import { type JSX, type ReactNode,useEffect, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { ErrorLogPayload, ErrorLogSource } from '../services/errorHandling/errorHandling.types';
import { hasEmailedErrorLog, isPreLoginPageRoute, markErrorLogEmailed } from '../services/errorLogger';
import { AccessRestrictedIllustration } from './AccessRestrictedIllustration';
import { EmailErrorDialog } from './EmailErrorDialog';
import { CheckIcon, CopyIcon, EmailIcon, HomeIcon, RefreshIcon } from './icons';
import { PageNotFoundIllustration } from './PageNotFoundIllustration';
import { SomethingWentWrongIllustration } from './SomethingWentWrongIllustration';
import './errorPage.css';

const COPIED_LABEL_DURATION_MS = 1500;

/** Every non-`'api'` `ErrorLogSource` gets its own short tag for the "email this
 * error" subject, so nothing falls through with no tag at all. */
const SOURCE_TAGS: Record<Exclude<ErrorLogSource, 'api'>, string> = {
  'component-boundary': 'Component',
  'window-error': 'Window',
  'unhandled-rejection': 'Promise',
  'login-callback': 'Login',
  'query-error': 'Query',
  'ui-action': 'UI',
  'legacy-cfm-link': 'Legacy',
  'not-found': 'Not Found',
};

// Keeps the subject line readable in an inbox even when `route` is a long/unbounded
// value (e.g. Page Not Found's route is whatever arbitrary URL the visitor hit).
const MAX_EMAIL_SUBJECT_DETAIL_LENGTH = 50;

/** Truncates to at most `maxLength` characters, indicating truncation with a trailing
 * ellipsis counted within that length — mirrors the correlationId's own truncation
 * convention already used in the subject (see `emailSubject` below). */
function truncateSubjectDetail(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

/**
 * The short cause tag for the "email this error" subject — "AOSlogin error
 * {detail} - {code}", e.g. "500 on /rulefinder/search". A network failure (no
 * response received) takes priority over the status code since
 * `apiResponse.status` is null for it; every other source maps to its own tag
 * via `SOURCE_TAGS`. Appends the payload's own route when it's known, so
 * support can see at a glance where the error happened without opening the body.
 * The combined "{detail} on {route}" string is capped at
 * `MAX_EMAIL_SUBJECT_DETAIL_LENGTH` characters.
 */
function resolveEmailSubjectDetail(payload: Partial<ErrorLogPayload> | null | undefined): string {
  const detail =
    payload?.source === 'api'
      ? payload.api?.category === 'network'
        ? 'Network'
        : typeof payload.api?.apiResponse?.status === 'number'
          ? String(payload.api.apiResponse.status)
          : ''
      : payload?.source
        ? SOURCE_TAGS[payload.source]
        : '';

  const route = payload?.route;
  const combined = detail && route && route !== 'unknown' ? `${detail} on ${route}` : detail;
  return truncateSubjectDetail(combined, MAX_EMAIL_SUBJECT_DETAIL_LENGTH);
}

/** "16 Jul 2026, 14:32 UTC" */
function formatUtcTimestamp(date: Date): string {
  const day = date.getUTCDate();
  const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes} UTC`;
}

export interface ErrorStateCardProps {
  /** bootstrap-icons class, e.g. "bi-shield-lock" — used only when `illustration` is unset. */
  readonly icon: string;
  /** Small circular icon overlapping the main icon's corner — used only when `illustration` is unset. */
  readonly badgeIcon: string;
  /** When set, renders the matching decorative illustration instead of `icon`/`badgeIcon`. */
  readonly illustration?: 'sww' | 'access' | 'not-found';
  /** Hides the "email this error" icon next to Copy — the 404 mockup only offers
   * copy, since a missing page isn't a bug report worth attaching browser/console
   * details to. @default true */
  readonly showEmailAction?: boolean;
  readonly title: string;
  /** Plain string or rich content (e.g. SSO backend HTML with a mailto link). */
  readonly description: ReactNode;
  /** Reloads whatever page the user was on before landing here. Button is omitted entirely when this is left unset — e.g. 403, where there's nothing sensible to retry. */
  readonly retryLabel?: string;
  readonly onRetry?: () => void;
  /** Navigates to the category home page (dashboard). Omitted when unset. */
  readonly goHomeLabel?: string;
  readonly onGoHome?: () => void;
  readonly correlationId?: string;
  /** The exact payload logged for this error (see errorLogger.ts's getRememberedErrorLogPayload)
   * — used as the body of the "email this error" action. */
  readonly logPayload?: Partial<ErrorLogPayload> | null;
  /** 'h1' when this is the page's own heading; 'h2' when nested inside a dialog that already has its own title. */
  readonly headingLevel?: 'h1' | 'h2';
  readonly className?: string;
}

/**
 * The error card itself — no page chrome (background, logo, footer, "contact support"
 * line — those live at the page level, see ErrorPageShell). Every piece of copy and
 * iconography is a prop, so the same component renders the 403 / 500 / generic variants
 * (see errorPageVariant.util.ts). Visual parity target: aosphere-nextgenui-user's
 * ErrorStateCard (same illustrations, fonts, correlationId reference block).
 */
export function ErrorStateCard({
  icon,
  badgeIcon,
  illustration,
  title,
  description,
  retryLabel,
  onRetry,
  goHomeLabel,
  onGoHome,
  correlationId,
  logPayload,
  headingLevel = 'h1',
  className,
  showEmailAction = true,
}: ErrorStateCardProps): JSX.Element {
  const Heading = headingLevel;
  // Prefers the logged payload's own timestamp (stable across a refresh once
  // getOrCreateErrorLogPayload has remembered it) over "now", so the displayed
  // Ref./timestamp block doesn't drift on every reload of the same broken URL.
  const errorDate = useMemo(
    () => (typeof logPayload?.timestamp === 'string' ? new Date(logPayload.timestamp) : new Date()),
    [logPayload?.timestamp]
  );
  const timestamp = useMemo(() => formatUtcTimestamp(errorDate), [errorDate]);
  const [justCopied, setJustCopied] = useState(false);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [hasEmailed, setHasEmailed] = useState(() => !!correlationId && hasEmailedErrorLog(correlationId));
  // eslint-disable-next-line unicorn/no-useless-undefined -- React 19's useRef<T>() overload requires the argument
  const revertTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    return () => clearTimeout(revertTimeoutRef.current);
  }, []);

  useEffect(() => {
    // The redirect here is a soft, client-side route change — the browser never
    // reloads, so nothing tells a screen reader a new "page" appeared unless focus
    // actually moves here. tabIndex={-1} makes this focusable programmatically
    // without adding it to the normal Tab order.
    articleRef.current?.focus();
  }, []);

  function handleCopyReference(): void {
    if (!correlationId) {
      return;
    }
    void navigator.clipboard.writeText(`Ref: ${correlationId}\nTimestamp: ${timestamp}`);
    setJustCopied(true);
    clearTimeout(revertTimeoutRef.current);
    revertTimeoutRef.current = setTimeout(() => setJustCopied(false), COPIED_LABEL_DURATION_MS);
  }

  function handleOpenEmailDialog(): void {
    if (!correlationId || hasEmailed) {
      return;
    }
    setIsEmailDialogOpen(true);
  }

  function handleEmailSent(): void {
    if (!correlationId) {
      return;
    }
    markErrorLogEmailed(correlationId);
    setHasEmailed(true);
  }

  const emailSubjectDetail = resolveEmailSubjectDetail(logPayload);
  // Flags a report from an unauthenticated session — support should expect it to
  // lack a logged-in user, and can't ask "which account were you on".
  const isPreLoginReport = !!logPayload?.route && isPreLoginPageRoute(logPayload.route);
  // Subject stays short and easy to read in an inbox; the body (below) carries the full code.
  const emailSubject = correlationId
    ? `AOSlogin error${isPreLoginReport ? ' (pre-login)' : ''}${emailSubjectDetail ? ` - ${emailSubjectDetail}` : ''} - ${correlationId.slice(0, 8)}…`
    : '';
  const emailBody = JSON.stringify(
    logPayload ?? {
      correlationId,
      route: typeof window === 'undefined' ? undefined : window.location.pathname,
      pageUrl: typeof window === 'undefined' ? undefined : window.location.href,
      timestamp: errorDate.toISOString(),
    },
    null,
    2
  );

  return (
    <div className="d-flex flex-column align-items-center w-100" style={{ gap: 20 }}>
      <article
        ref={articleRef}
        tabIndex={-1}
        className={`bg-white text-center w-100 ${className ?? ''}`}
        style={{
          outline: 'none',
          borderRadius: 12,
          boxShadow: '0 16px 40px rgba(9,14,36,0.14)',
          padding: '40px 36px 32px',
        }}
      >
        {illustration === 'sww' ? (
          <SomethingWentWrongIllustration style={{ height: 'auto', width: 200, marginBottom: 20 }} />
        ) : illustration === 'access' ? (
          <AccessRestrictedIllustration style={{ height: 'auto', width: 200, marginBottom: 20 }} />
        ) : illustration === 'not-found' ? (
          <PageNotFoundIllustration style={{ height: 'auto', width: 200, marginBottom: 20 }} />
        ) : (
          <div
            className="position-relative d-inline-flex align-items-center justify-content-center"
            style={{ width: 64, height: 64, marginBottom: 20 }}
          >
            <div
              className="d-flex align-items-center justify-content-center w-100 h-100"
              style={{ border: '1px solid #e2e8f0', borderRadius: 12 }}
            >
              <i className={`bi ${icon} fs-3`} style={{ color: '#334155' }} />
            </div>
            <span
              className="position-absolute rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 24, height: 24, right: -6, bottom: -6, backgroundColor: '#1e293b' }}
            >
              <i className={`bi ${badgeIcon} text-white`} style={{ fontSize: '0.7rem' }} />
            </span>
          </div>
        )}

        <Heading className="ao-errorCard-title mb-0" style={{ marginTop: 10, fontSize: '1.5rem', lineHeight: '32px', color: '#262727', fontWeight: 600 }}>
          {title}
        </Heading>

        <div className="ao-errorCard-body mx-auto" style={{ marginTop: 10, maxWidth: 340, color: '#4d5454', fontSize: '15px', lineHeight: '22px' }}>
          {description}
        </div>

        <div className="d-flex mt-4" style={{ gap: 12 }}>
          {retryLabel && onRetry && (
            <button
              type="button"
              className={`ao-action-btn ao-errorCard-body d-inline-flex align-items-center justify-content-center ${goHomeLabel ? 'flex-fill' : 'w-100'}`}
              onClick={onRetry}
            >
              <RefreshIcon className="ao-btn-icon me-2" />
              {retryLabel}
            </button>
          )}
          {goHomeLabel && onGoHome && (
            <button
              type="button"
              className="ao-action-btn ao-errorCard-body d-inline-flex align-items-center justify-content-center flex-fill"
              onClick={onGoHome}
            >
              {goHomeLabel !== 'OK' && <HomeIcon className="ao-btn-icon me-2" />}
              {goHomeLabel}
            </button>
          )}
        </div>

        {correlationId && (
          <>
            <hr className="border-top" style={{ borderColor: '#e2e8f0', margin: '24px 0 16px' }} />

            <div className="ao-errorCard-mono" style={{ color: '#64748b', fontSize: '11.5px' }}>
              <p className="mb-1 d-flex align-items-center justify-content-center" style={{ gap: 6 }}>
                <span>Ref. {correlationId}</span>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="error-copy-reference-tooltip">{justCopied ? 'Copied!' : 'Copy reference and timestamp'}</Tooltip>}
                >
                  <button
                    type="button"
                    className="ao-icon-btn ao-copy-icon-btn"
                    aria-label={justCopied ? 'Reference and timestamp copied' : 'Copy reference and timestamp'}
                    onClick={handleCopyReference}
                  >
                    {justCopied ? (
                      <CheckIcon className="ao-small-icon" />
                    ) : (
                      <CopyIcon />
                    )}
                  </button>
                </OverlayTrigger>
              </p>
              <p className="mb-0">{timestamp}</p>
            </div>
          </>
        )}
      </article>

      {correlationId && showEmailAction && (
        <p
          className="ao-errorCard-body mb-0 d-flex align-items-center justify-content-center"
          style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12.5px', gap: 6 }}
        >
          <span>Email these details to our support team.</span>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="error-email-tooltip">{hasEmailed ? 'Error details already sent' : 'Email error details'}</Tooltip>}
          >
            <span className="d-inline-block">
              <button
                type="button"
                className="ao-icon-btn ao-email-icon-btn"
                style={{
                  opacity: hasEmailed || isEmailDialogOpen ? 0.5 : undefined,
                  pointerEvents: hasEmailed || isEmailDialogOpen ? 'none' : undefined,
                }}
                disabled={hasEmailed || isEmailDialogOpen}
                aria-label={hasEmailed ? 'Error details already sent' : 'Email error details'}
                onClick={handleOpenEmailDialog}
              >
                <EmailIcon className="ao-small-icon" />
              </button>
            </span>
          </OverlayTrigger>
        </p>
      )}

      {correlationId && showEmailAction && (
        <EmailErrorDialog
          show={isEmailDialogOpen}
          correlationId={correlationId}
          subject={emailSubject}
          body={emailBody}
          onHide={() => setIsEmailDialogOpen(false)}
          onSent={handleEmailSent}
        />
      )}
    </div>
  );
}
